import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import { tooltipClasses } from '@mui/material/Tooltip'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TenantSearchItem } from '@/common/components/SideBar/SidebarComponents/TenantSearchItem/TenantSearchItem'
import { useNavigate } from '@/hooks'
import CustomPalette from '@/theme/CustomPalette'
import {
  bottomAccountSectionStyles,
  collapsedItemButtonStyles,
  collapsedItemIconStyles,
  collapsedSectionInitialStyles,
  itemButtonStyles,
  itemIconStyles,
  itemTextStyles,
  sectionDividerStyles,
  sectionHeaderButtonStyles,
  sectionHeaderContentStyles,
  sectionSubtitleStyles,
  sectionTitleStyles,
  sidebarContainerStyles,
  userNameContainerStyles,
  userNameStyles,
} from './NavBarStyles'
import {
  customerManagementItems,
  globalAdministrationItems,
  learningManagementItems,
  myAccountItems,
  getMenuItemIcon,
  getSectionInitial,
} from './NavBarHelpers'
import type { SidebarItemKey, SidebarProps, SidebarItem, SidebarSection, SidebarSectionKey } from './NavBarHelpers'

export type { SidebarItemKey, SidebarSectionKey, SidebarItem }

const temporarilyDisabledItemKeys = new Set<SidebarItemKey>()

type SectionTitleProps = {
  title: string
}

const sectionTitleTooltipSlotProps = {
  popper: {
    sx: {
      [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
        marginLeft: '55px',
      },
    },
  },
}

const SectionTitle = ({ title }: SectionTitleProps): JSX.Element => {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const titleRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!titleRef.current) return

    const checkOverflow = () => {
      if (!titleRef.current) return
      setIsOverflowing(titleRef.current.scrollWidth > titleRef.current.clientWidth)
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [title])

  const titleContent = (
    <Typography ref={titleRef} sx={sectionTitleStyles}>
      {title}
    </Typography>
  )

  if (!isOverflowing) {
    return titleContent
  }

  return (
    <Tooltip title={title} placement="right" arrow slotProps={sectionTitleTooltipSlotProps}>
      <Box component="span">{titleContent}</Box>
    </Tooltip>
  )
}

export const NavBar = ({
  companyName,
  userName,
  activeItem = 'reportingLicenses',
  onNavigate,
  initialOpenSection = 'globalAdministration',
  isOpen = true,
  visibleItems,
  tenantSubdomain = '',
  tenantsList = [],
  onForceOpen,
}: SidebarProps): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const navigate = useNavigate()
  const [openSection, setOpenSection] = useState<SidebarSectionKey | null>(initialOpenSection)
  const [shouldOpenTenantSearch, setShouldOpenTenantSearch] = useState(false)
  const isCollapsed = !isOpen
  const visibleItemsSet = useMemo(() => (visibleItems ? new Set(visibleItems) : null), [visibleItems])
  const selectedTenantName = useMemo(() => {
    const selectedTenant = tenantsList.find((tenant) => tenant.subdomain === tenantSubdomain)
    return selectedTenant?.name || companyName || ''
  }, [companyName, tenantSubdomain, tenantsList])

  useEffect(() => {
    setOpenSection(initialOpenSection)
  }, [initialOpenSection])

  const sections = useMemo<SidebarSection[]>(
    () => [
      {
        key: 'customerManagement',
        title: t('backOffice_sidebar'),
        items: customerManagementItems
          .filter((item) => !visibleItemsSet || visibleItemsSet.has(item.key))
          .map((item) => ({
            ...item,
            label: t(item.label),
          })),
      },
      {
        key: 'globalAdministration',
        title: companyName || '',
        subtitle: String(t('globalAdministration')),
        items: globalAdministrationItems
          .filter((item) => !visibleItemsSet || visibleItemsSet.has(item.key))
          .map((item) => ({
            ...item,
            label: t(item.label),
          })),
      },
      {
        key: 'learningManagement',
        title: companyName || '',
        subtitle: String(t('learningManagement')),
        items: learningManagementItems
          .filter((item) => !visibleItemsSet || visibleItemsSet.has(item.key))
          .map((item) => ({
            ...item,
            // The Trainings experience is titled "Courses" now (see Trainings.tsx).
            label: t(item.key === 'learningTrainings' ? 'cycle_overview_page_title' : item.label),
          })),
      },
      {
        key: 'myAccount',
        title: t('myAccount'),
        items: myAccountItems
          .filter((item) => !visibleItemsSet || visibleItemsSet.has(item.key))
          .map((item) => ({
            ...item,
            label: t(item.label),
          })),
      },
    ],
    [companyName, visibleItemsSet, t],
  )

  const topSections = sections.filter((section) => section.key !== 'myAccount')
  const myAccountSection = sections.find((section) => section.key === 'myAccount')
  const visibleTopSections = topSections.filter((section) => section.items.length > 0)
  const visibleMyAccountSection = myAccountSection && myAccountSection.items.length > 0 ? myAccountSection : undefined
  const shouldRenderMyAccountOnTop = visibleTopSections.length === 0 && Boolean(visibleMyAccountSection)
  const topSectionsToRender =
    shouldRenderMyAccountOnTop && visibleMyAccountSection ? [visibleMyAccountSection] : visibleTopSections

  const handleToggleSection = (sectionKey: SidebarSectionKey) => {
    setOpenSection((previousSection) => (previousSection === sectionKey ? null : sectionKey))
  }

  const handleLinkNavigation = (event: MouseEvent<HTMLAnchorElement>) => {
    navigate(event)
    if (onNavigate) {
      onNavigate()
    }
  }

  const handleCollapsedItemClick = (event: MouseEvent<HTMLAnchorElement>, item: SidebarItem) => {
    if (item.key === 'dashboard' && onForceOpen && tenantSubdomain) {
      event.preventDefault()
      onForceOpen()
      setOpenSection('customerManagement')
      setShouldOpenTenantSearch(true)
      return
    }

    if (item.path) {
      handleLinkNavigation(event)
    }
  }

  const isItemDisabled = (item: SidebarItem) => temporarilyDisabledItemKeys.has(item.key)

  const getCollapsedItemLabel = (item: SidebarItem) =>
    item.key === 'dashboard' && selectedTenantName ? selectedTenantName : item.label

  const renderCollapsedItemButton = (item: SidebarItem) => {
    const isDisabled = isItemDisabled(item) || !item.path
    const itemLabel = getCollapsedItemLabel(item)

    if (isDisabled) {
      return (
        <ListItemButton aria-label={itemLabel} disabled selected={false} sx={collapsedItemButtonStyles}>
          <ListItemIcon sx={collapsedItemIconStyles}>{getMenuItemIcon(item.key, true)}</ListItemIcon>
        </ListItemButton>
      )
    }

    return (
      <ListItemButton
        aria-label={itemLabel}
        component="a"
        href={item.path}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => handleCollapsedItemClick(event, item)}
        selected={activeItem === item.key}
        sx={collapsedItemButtonStyles}>
        <ListItemIcon sx={collapsedItemIconStyles}>{getMenuItemIcon(item.key, true)}</ListItemIcon>
      </ListItemButton>
    )
  }

  const renderExpandedItemButton = (item: SidebarItem) => {
    const isDisabled = isItemDisabled(item) || !item.path

    if (isDisabled) {
      return (
        <ListItemButton disabled selected={false} sx={itemButtonStyles}>
          <ListItemIcon sx={itemIconStyles}>{getMenuItemIcon(item.key, false)}</ListItemIcon>
          <ListItemText primary={item.label} sx={itemTextStyles} />
        </ListItemButton>
      )
    }

    return (
      <ListItemButton
        component="a"
        href={item.path}
        onClick={handleLinkNavigation}
        selected={activeItem === item.key}
        sx={itemButtonStyles}>
        <ListItemIcon sx={itemIconStyles}>{getMenuItemIcon(item.key, false)}</ListItemIcon>
        <ListItemText primary={item.label} sx={itemTextStyles} />
      </ListItemButton>
    )
  }

  const renderCollapsedItem = (item: SidebarItem) => {
    const isDisabled = isItemDisabled(item) || !item.path
    const itemButton = renderCollapsedItemButton(item)
    const tooltipTitle = getCollapsedItemLabel(item)

    return (
      <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
        <Tooltip title={tooltipTitle} placement="right" arrow>
          {isDisabled ? (
            <Box component="span" sx={{ display: 'block' }}>
              {itemButton}
            </Box>
          ) : (
            itemButton
          )}
        </Tooltip>
      </ListItem>
    )
  }

  if (isCollapsed) {
    return (
      <Box sx={sidebarContainerStyles} data-testid="global-sidebar-container">
        <Box sx={{ pt: '0px' }}>
          {topSectionsToRender.map((section, index) => (
            <Box key={section.key}>
              <Box sx={{ py: 1 }}>
                <Typography sx={collapsedSectionInitialStyles}>
                  {getSectionInitial(section.key, i18n.language)}
                </Typography>
              </Box>
              <List disablePadding>{section.items.map((item) => renderCollapsedItem(item))}</List>
              {index < topSectionsToRender.length - 1 && <Divider sx={sectionDividerStyles} />}
            </Box>
          ))}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {visibleMyAccountSection && !shouldRenderMyAccountOnTop && (
          <Box sx={bottomAccountSectionStyles} data-testid="global-sidebar-bottom-account">
            <Box sx={{ py: 1 }}>
              <Typography sx={collapsedSectionInitialStyles}>
                {getSectionInitial('myAccount', i18n.language)}
              </Typography>
            </Box>
            <List disablePadding>{visibleMyAccountSection.items.map((item) => renderCollapsedItem(item))}</List>
          </Box>
        )}
      </Box>
    )
  }

  const sectionIcon = (sectionKey: SidebarSectionKey) =>
    openSection === sectionKey ? (
      <KeyboardArrowUpIcon sx={{ color: CustomPalette.SecondaryNightBlue600, fontSize: '20px' }} />
    ) : (
      <KeyboardArrowDownIcon sx={{ color: CustomPalette.SecondaryNightBlue600, fontSize: '20px' }} />
    )

  const renderSection = (section: SidebarSection) => (
    <Box key={section.key}>
      <ListItemButton
        onClick={() => handleToggleSection(section.key)}
        sx={sectionHeaderButtonStyles}
        aria-expanded={openSection === section.key}>
        <Box sx={sectionHeaderContentStyles}>
          <SectionTitle title={section.title} />
          {section.subtitle && <Typography sx={sectionSubtitleStyles}>{section.subtitle}</Typography>}
        </Box>
        {sectionIcon(section.key)}
      </ListItemButton>

      <Collapse in={openSection === section.key} timeout={250} unmountOnExit>
        {section.items.length > 0 && (
          <List disablePadding>
            {section.items.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ display: 'block' }}>
                {section.key === 'customerManagement' && item.key === 'dashboard' && tenantSubdomain ? (
                  <TenantSearchItem
                    isNavBarOpen={true}
                    subdomain={tenantSubdomain}
                    tenantsList={tenantsList}
                    handleForceOpen={onForceOpen || (() => undefined)}
                    shouldOpenOnMount={shouldOpenTenantSearch}
                    onOpenOnMountHandled={() => setShouldOpenTenantSearch(false)}
                  />
                ) : (
                  renderExpandedItemButton(item)
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Collapse>

      {section.key !== 'myAccount' && <Divider sx={sectionDividerStyles} />}
    </Box>
  )

  return (
    <Box sx={sidebarContainerStyles} data-testid="global-sidebar-container">
      <List disablePadding sx={{ pt: '4px' }}>
        {topSectionsToRender.map((section) => renderSection(section))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {visibleMyAccountSection && !shouldRenderMyAccountOnTop && (
        <List disablePadding data-testid="global-sidebar-bottom-account" sx={bottomAccountSectionStyles}>
          {renderSection(visibleMyAccountSection)}
        </List>
      )}

      <Box sx={userNameContainerStyles}>
        <Typography sx={userNameStyles}>{userName}</Typography>
      </Box>
    </Box>
  )
}

export default NavBar
